/**
 * "Find my size" for the product page.
 *
 * ── How the recommendation works ──────────────────────────────────────
 * The owner's table, in three steps, over the ladder S-M-L-XL-XXL:
 *
 *   1. Base size from weight:
 *        up to 64 kg      -> S
 *        65-79 kg         -> M
 *        80-92 kg         -> L
 *        93-105 kg        -> XL
 *        106 kg and up    -> XXL
 *
 *   2. Height adjustment:
 *        up to 159 cm     -> one size down
 *        160-179 cm       -> no change
 *        180 cm and up    -> one size up
 *
 *   3. Requested fit:
 *        tighter / fitted -> no change
 *        oversized        -> one size up
 *        very oversized   -> two sizes up
 *
 * The result is clamped to the S..XXL ladder, and then mapped onto the
 * sizes this particular product really sells: "XXL" and "2XL" are the same
 * rung, and when the recommended rung does not exist on the product the
 * shopper is moved to the closest one that does - upwards, matching the
 * "between two sizes, take the bigger one" note in the dialog.
 */

/** The canonical ladder the table above is written against. */
const SIZE_LADDER = ['S', 'M', 'L', 'XL', 'XXL'];

/** Rung on the ladder for every spelling of a size we might meet. */
const SIZE_RANKS = {
  XXXS: -3,
  '3XS': -3,
  XXS: -2,
  '2XS': -2,
  XS: -1,
  '1XS': -1,
  S: 0,
  SMALL: 0,
  M: 1,
  MEDIUM: 1,
  L: 2,
  LARGE: 2,
  XL: 3,
  '1XL': 3,
  XLARGE: 3,
  XXL: 4,
  '2XL': 4,
  XXLARGE: 4,
  XXXL: 5,
  '3XL': 5,
  XXXXL: 6,
  '4XL': 6,
};

/** Fit slider position -> how many sizes to add. */
const FIT_OFFSETS = [0, 1, 2];
const FIT_WORDS = ['צמודה יותר', 'אוברסייז', 'מאוד אוברסייז'];

/**
 * @param {string} value - a size as the product spells it
 * @returns {number|null} its rung on the ladder, or null if unrecognised
 */
function rankOf(value) {
  const key = String(value).trim().toUpperCase().replace(/[\s._-]/g, '');
  return key in SIZE_RANKS ? SIZE_RANKS[key] : null;
}

/**
 * How many sizes the requested fit adds. Accepts the slider position and
 * the written values, so the dialog can change without touching this.
 *
 * @param {number|string} fit
 */
function fitOffset(fit) {
  if (typeof fit === 'number' || /^\d+$/.test(String(fit).trim())) {
    return FIT_OFFSETS[Number(fit)] ?? 0;
  }

  const key = String(fit).trim().toLowerCase();
  if (
    key === 'very-oversized' ||
    key === 'oversized-very' ||
    key === 'very_oversized' ||
    key === 'אוברסייז מאוד' ||
    key === 'מאוד אוברסייז'
  ) {
    return 2;
  }
  if (key === 'oversized' || key === 'אוברסייז') return 1;

  // "צמוד" / "צמוד יותר" and anything unknown: no change.
  return 0;
}

/**
 * The owner's table. Returns a size from S..XXL.
 *
 * @param {number} weight - kg
 * @param {number} height - cm
 * @param {number|string} fit
 */
export function calculateRecommendedSize(weight, height, fit) {
  let sizeIndex;

  // Step 1 - base size from weight.
  if (weight <= 64) {
    sizeIndex = 0; // S
  } else if (weight <= 79) {
    sizeIndex = 1; // M
  } else if (weight <= 92) {
    sizeIndex = 2; // L
  } else if (weight <= 105) {
    sizeIndex = 3; // XL
  } else {
    sizeIndex = 4; // XXL
  }

  // Step 2 - height.
  if (height <= 159) {
    sizeIndex -= 1;
  } else if (height >= 180) {
    sizeIndex += 1;
  }

  // Step 3 - requested fit.
  sizeIndex += fitOffset(fit);

  // Clamp to S..XXL.
  sizeIndex = Math.max(0, Math.min(SIZE_LADDER.length - 1, sizeIndex));

  return SIZE_LADDER[sizeIndex];
}

/**
 * Map the recommended rung onto the sizes this product actually has.
 *
 * @param {string[]} sizes - the option values, in whatever order Shopify gives them
 * @param {number} height - cm
 * @param {number} weight - kg
 * @param {number|string} fit
 */
export function recommend(sizes, height, weight, fit) {
  const target = rankOf(calculateRecommendedSize(weight, height, fit));

  const ranked = sizes
    .map((value) => ({ value, rank: rankOf(value) }))
    .filter((entry) => entry.rank !== null)
    .sort((a, b) => a.rank - b.rank);

  // No recognisable sizes (a product using words, say): fall back to the
  // order Shopify gave us so the dialog still answers something sensible.
  if (!ranked.length) {
    const index = Math.min(sizes.length - 1, Math.max(0, target));
    return { size: sizes[index], atTop: false, atBottom: false };
  }

  const smallest = ranked[0];
  const largest = ranked[ranked.length - 1];

  if (target <= smallest.rank) {
    return { size: smallest.value, atTop: false, atBottom: target < smallest.rank };
  }
  if (target >= largest.rank) {
    return { size: largest.value, atTop: target > largest.rank, atBottom: false };
  }

  const exact = ranked.find((entry) => entry.rank === target);
  if (exact) return { size: exact.value, atTop: false, atBottom: false };

  // A hole in the product's ladder - round up, per the dialog's own advice.
  const next = ranked.find((entry) => entry.rank > target);
  return { size: next.value, atTop: false, atBottom: false };
}

/** @param {HTMLElement} root */
function setup(root) {
  const modal = root.querySelector('[data-size-finder-modal]');
  if (!modal) return;

  const sizes = (modal.dataset.sizes || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (sizes.length < 2) return;

  const heightInput = modal.querySelector('[data-height]');
  const weightInput = modal.querySelector('[data-weight]');
  const fitInput = modal.querySelector('[data-fit]');
  const heightOut = modal.querySelector('[data-height-out]');
  const weightOut = modal.querySelector('[data-weight-out]');
  const ticks = [...modal.querySelectorAll('[data-fit-tick]')];
  const form = modal.querySelector('[data-size-finder-form]');
  const result = modal.querySelector('[data-size-finder-result]');
  const resultSize = modal.querySelector('[data-result-size]');
  const resultNote = modal.querySelector('[data-result-note]');

  let lastFocused = null;

  /** Paint the filled part of a range track, which browsers do not do for us. */
  const paintTrack = (input) => {
    const min = Number(input.min);
    const max = Number(input.max);
    const pct = max === min ? 0 : ((Number(input.value) - min) / (max - min)) * 100;
    input.style.setProperty('--fill', `${pct}%`);
  };

  const syncLabels = () => {
    heightOut.textContent = `${heightInput.value} ס״מ`;
    weightOut.textContent = `${weightInput.value} ק״ג`;
    const fit = Number(fitInput.value);
    ticks.forEach((t) => {
      t.classList.toggle('is-active', Number(t.dataset.fitTick) === fit);
    });
    [heightInput, weightInput, fitInput].forEach(paintTrack);
  };

  [heightInput, weightInput, fitInput].forEach((input) => {
    input.addEventListener('input', syncLabels);
  });
  syncLabels();

  const open = () => {
    lastFocused = document.activeElement;
    modal.hidden = false;
    // Let the browser paint the hidden state first so the transition runs.
    requestAnimationFrame(() => modal.classList.add('size-finder__modal--open'));
    document.documentElement.style.setProperty('overflow', 'hidden');
    heightInput.focus({ preventScroll: true });
  };

  const close = () => {
    modal.classList.remove('size-finder__modal--open');
    document.documentElement.style.removeProperty('overflow');
    const done = () => {
      modal.hidden = true;
      modal.removeEventListener('transitionend', done);
    };
    modal.addEventListener('transitionend', done);
    // Fallback for reduced-motion, where no transition fires.
    setTimeout(done, 420);
    if (lastFocused instanceof HTMLElement) lastFocused.focus({ preventScroll: true });
  };

  const show = () => {
    const { size, atTop, atBottom } = recommend(
      sizes,
      Number(heightInput.value),
      Number(weightInput.value),
      Number(fitInput.value)
    );

    resultSize.textContent = size;
    resultSize.dataset.size = size;

    const fitWord = FIT_WORDS[Number(fitInput.value)] ?? FIT_WORDS[1];
    let note = `לגזרה ${fitWord} בגובה ${heightInput.value} ס״מ ומשקל ${weightInput.value} ק״ג.`;
    if (atTop) note += ` זו המידה הגדולה ביותר שיש לנו בדגם הזה.`;
    if (atBottom) note += ` זו המידה הקטנה ביותר שיש לנו בדגם הזה.`;
    resultNote.textContent = note;

    form.hidden = true;
    result.hidden = false;
  };

  const apply = () => {
    const size = resultSize.dataset.size;
    if (!size) return close();

    // The variant radios carry the plain option value, so matching on it is
    // enough and survives the picker being re-rendered by the theme.
    const radios = [...document.querySelectorAll('input[type="radio"][value]')].filter(
      (r) => r.value.trim() === size && r.closest('.variant-option')
    );
    const target = radios.find((r) => r.dataset.optionAvailable !== 'false') || radios[0];

    if (target && !target.checked) {
      target.click();
    }
    close();
  };

  root.querySelector('[data-size-finder-open]')?.addEventListener('click', open);
  modal.querySelectorAll('[data-size-finder-close]').forEach((el) => {
    el.addEventListener('click', close);
  });
  modal.querySelector('[data-size-finder-submit]')?.addEventListener('click', show);
  modal.querySelector('[data-size-finder-apply]')?.addEventListener('click', apply);
  modal.querySelector('[data-size-finder-again]')?.addEventListener('click', () => {
    result.hidden = true;
    form.hidden = false;
  });

  modal.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') close();
  });
}

document.querySelectorAll('[data-size-finder]').forEach(setup);
